import { useCallback, useEffect, useState } from "react";
import {
  LabellerCostStatsResponse,
  useGetLabellerCostStatsQuery,
} from "src/store/api/generatedApi";
import { DateRangeFilterState } from "components/manageLabellers/dateRangeFilterBar";

export type TenantName = string;

export type TenantStatsState = Readonly<{
  response: LabellerCostStatsResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}>;

export type OnTenantStatsLoaded = (
  tenantName: TenantName,
  response: LabellerCostStatsResponse | undefined,
  isLoading: boolean,
  isFetching: boolean,
  isError: boolean
) => void;

export const sumNullable = (
  a: number | null,
  b: number | null
): number | null => (a == null && b == null ? null : (a ?? 0) + (b ?? 0));

const isShallowEqual = <TState extends object>(a: TState, b: TState): boolean =>
  Object.keys(a).every(
    (key) => a[key as keyof TState] === b[key as keyof TState]
  );

export const mergeTenantState = <TState extends object>(
  previous: Record<TenantName, TState>,
  tenantName: TenantName,
  next: TState
): Record<TenantName, TState> => {
  const existing = previous[tenantName];
  return existing !== undefined && isShallowEqual(existing, next)
    ? previous
    : { ...previous, [tenantName]: next };
};

// A tenant with no entry has not reported through its loader's effect yet, so
// it counts as loading rather than as an empty result.
export const useTenantLabellerStats = (tenantNames: readonly string[]) => {
  const [stateByTenant, setStateByTenant] = useState<
    Record<TenantName, TenantStatsState>
  >({});

  const onTenantStatsLoaded: OnTenantStatsLoaded = useCallback(
    (tenantName, response, isLoading, isFetching, isError) => {
      setStateByTenant((previous) =>
        mergeTenantState(previous, tenantName, {
          response,
          isLoading,
          isFetching,
          isError,
        })
      );
    },
    []
  );

  return {
    stateByTenant,
    anyLoading: tenantNames.some(
      (name) => stateByTenant[name]?.isLoading ?? true
    ),
    anyFetching: tenantNames.some(
      (name) => stateByTenant[name]?.isFetching ?? true
    ),
    failedTenantNames: tenantNames.filter(
      (name) => stateByTenant[name]?.isError ?? false
    ),
    onTenantStatsLoaded,
  };
};

type TenantLabellerStatsLoaderProps = Readonly<{
  tenantName: TenantName;
  dateFilters: DateRangeFilterState;
  onLoaded: OnTenantStatsLoaded;
}>;

export const TenantLabellerStatsLoader = ({
  tenantName,
  dateFilters,
  onLoaded,
}: TenantLabellerStatsLoaderProps) => {
  const { currentData, isLoading, isFetching, isError } =
    useGetLabellerCostStatsQuery({
      tenantName,
      start: dateFilters.start?.toISOString() ?? undefined,
      end: dateFilters.end?.toISOString() ?? undefined,
      filterMode: dateFilters.filterMode,
    });

  useEffect(() => {
    onLoaded(tenantName, currentData, isLoading, isFetching, isError);
  }, [tenantName, currentData, isLoading, isFetching, isError, onLoaded]);

  return null;
};
